/**
 * InputModeRenderer - Prompt node input mode configuration
 *
 * Handles the input mode toggle (manual/agent/llm) and shows
 * the corresponding configuration fields for each mode.
 *
 * Output Schema is a standalone selector that affects all modes:
 * - Manual: Schema fields become input form (user fills in values)
 * - Agent/LLM: Schema fields become outputs (AI generates them)
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Bot, Cpu, Keyboard, ChevronDown, ChevronUp, FileJson, Plus } from 'lucide-react';
import type { PromptInputMode } from '@/types/imageWorkflow';
import type { OutputFieldDefinition, ObjectKeyInfo, FieldType } from '@/types/workflowFields';
import { getFieldTypeColor } from '@/types/workflowFields';
import { usePlaygroundAgents, usePromptAssistTemplate } from '@/features/playground/hooks/usePlayground';
import { useChatModelsQuery } from '@/lib/queries';
import {
  useAvailableOutputSchemas,
  useOutputSchema,
} from '@/features/system-settings/hooks/useOutputSchemas';
import { PromptTemplateSelector } from '@/features/system-settings';
import type { BaseCustomRendererProps } from './types';

export const InputModeRenderer: React.FC<BaseCustomRendererProps> = ({
  nodeData,
  isDisabled = false,
  onBatchUpdate,
}) => {
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);

  // Fetch data
  const { data: agents = [], isLoading: agentsLoading } = usePlaygroundAgents();
  const { data: chatModels = [], isLoading: chatModelsLoading } = useChatModelsQuery();
  const { data: promptTemplate } = usePromptAssistTemplate();

  // Output schema - available for ALL modes (not just agent)
  const { data: allSchemasData, isLoading: schemasLoading } = useAvailableOutputSchemas();
  const allSchemas = allSchemasData?.schemas || [];

  // Get full schema to derive fields
  const selectedOutputSchemaKey = nodeData.output_schema_key as string | undefined;
  const { data: fullSchema } = useOutputSchema(selectedOutputSchemaKey || '');

  // Helper function to map JSON Schema type to FieldType
  const mapJsonSchemaType = (type?: string): FieldType => {
    if (type === 'number' || type === 'integer') return 'number';
    if (type === 'boolean') return 'boolean';
    if (type === 'array') return 'array';
    if (type === 'object') return 'object';
    return 'string';
  };

  // Derive schema fields with metadata
  const schemaFields = useMemo(() => {
    if (!fullSchema?.json_schema) return [];

    const jsonSchema = fullSchema.json_schema as {
      properties?: Record<string, {
        type?: string;
        description?: string;
        properties?: Record<string, { type?: string; description?: string }>;
      }>;
      required?: string[];
    };

    if (!jsonSchema.properties) return [];
    const requiredFields = jsonSchema.required || [];

    return Object.entries(jsonSchema.properties).map(([key, prop]) => {
      const fieldType = mapJsonSchemaType(prop.type);

      // For object-type fields, extract sub-keys from properties
      let objectKeys: ObjectKeyInfo[] | undefined;
      if (prop.type === 'object' && prop.properties) {
        objectKeys = Object.entries(prop.properties).map(([subKey, subProp]) => ({
          key: subKey,
          type: mapJsonSchemaType(subProp.type),
          label: subKey,
          description: subProp.description,
        }));
      }

      return {
        key,
        type: fieldType,
        // Use key directly as label - no automatic case conversion
        // (label and key are shown together in UI for clarity)
        label: key,
        description: prop.description,
        required: requiredFields.includes(key),
        indexed: false,
        output_schema_key: fullSchema.key,
        object_keys: objectKeys,
      };
    });
  }, [fullSchema]);

  const inputMode = (nodeData.input_mode || 'manual') as PromptInputMode;
  const hasSchemaSelected = !!selectedOutputSchemaKey && schemaFields.length > 0;

  // Get custom output fields that need manual input (no value_source_input_key)
  // These are fields added by user via the + button in OutputFieldsSection
  const customOutputFieldsWithoutSource = useMemo(() => {
    const customFields = (nodeData.output_fields || []) as OutputFieldDefinition[];
    return customFields.filter(f => !f.value_source_input_key);
  }, [nodeData.output_fields]);

  // Update schema_output_fields when schema changes
  // Schema fields become outputs regardless of mode (so downstream nodes can map to them)
  // - In Agent/LLM mode: AI generates the values
  // - In Manual mode: User fills in the values, but they're still outputs
  useEffect(() => {
    if (onBatchUpdate) {
      const currentSchemaFieldsJson = JSON.stringify(nodeData.schema_output_fields || []);
      const newSchemaFieldsJson = JSON.stringify(schemaFields);

      if (newSchemaFieldsJson !== currentSchemaFieldsJson) {
        onBatchUpdate({
          schema_output_fields: schemaFields.length > 0 ? schemaFields : undefined,
        });
      }
    }
  }, [schemaFields, onBatchUpdate, nodeData.schema_output_fields]);

  const handleModeChange = (mode: PromptInputMode) => {
    if (onBatchUpdate) {
      onBatchUpdate({ input_mode: mode });
    }
  };

  const handleUpdate = (field: string, value: unknown) => {
    if (onBatchUpdate) {
      onBatchUpdate({ [field]: value });
    }
  };

  const handleSchemaChange = (schemaKey: string | undefined) => {
    if (onBatchUpdate) {
      onBatchUpdate({ output_schema_key: schemaKey });
    }
  };

  return (
    <div className="space-y-4">
      {/* Input Mode Toggle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">生成方式</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleModeChange('manual')}
            disabled={isDisabled}
            className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              inputMode === 'manual'
                ? 'bg-primary-100 border-primary-500 text-primary-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Keyboard className="w-4 h-4" />
            <span>手动</span>
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('agent')}
            disabled={isDisabled}
            className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              inputMode === 'agent'
                ? 'bg-primary-100 border-primary-500 text-primary-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Bot className="w-4 h-4" />
            <span>Agent</span>
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('llm')}
            disabled={isDisabled}
            className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              inputMode === 'llm'
                ? 'bg-primary-100 border-primary-500 text-primary-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Cpu className="w-4 h-4" />
            <span>LLM</span>
          </button>
        </div>
      </div>

      {/* Output Schema Selector - Standalone, affects all modes */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <FileJson className="w-4 h-4 text-blue-600" />
          <label className="text-sm font-medium text-blue-700">
            输出格式
            {schemasLoading && <span className="ml-2 text-blue-400 text-xs">加载中...</span>}
          </label>
        </div>
        <select
          value={selectedOutputSchemaKey || 'none'}
          onChange={(e) => {
            const value = e.target.value;
            handleSchemaChange(value === 'none' ? undefined : value);
          }}
          disabled={isDisabled || schemasLoading}
          className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 bg-white"
        >
          <option value="none">无格式约束 (自由输出)</option>
          {allSchemas.map((schema) => (
            <option key={schema.key} value={schema.key}>
              {schema.name_zh || schema.name} ({schema.key})
            </option>
          ))}
        </select>
        <p className="text-xs text-blue-600 mt-1.5">
          {inputMode === 'manual'
            ? '手动模式: 选择格式后，将显示对应的输入表单'
            : 'Agent/LLM 模式: 选择格式后，AI 将按指定结构输出'}
        </p>
      </div>

      {/* Manual Mode */}
      {inputMode === 'manual' && (
        hasSchemaSelected ? (
          // Schema selected: Render schema fields as input form
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <span>按格式填写</span>
              <span className="text-xs text-gray-400">({fullSchema?.name_zh || fullSchema?.name})</span>
            </div>
            {schemaFields.map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                  {/* Only show key in parentheses if different from label */}
                  {field.label !== field.key && (
                    <span className="text-xs text-gray-400 font-normal ml-1">({field.key})</span>
                  )}
                  {field.required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                {field.type === 'string' ? (
                  <textarea
                    value={(nodeData[field.key] as string) || ''}
                    onChange={(e) => handleUpdate(field.key, e.target.value)}
                    rows={2}
                    disabled={isDisabled}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                    placeholder={field.description || `输入 ${field.label}...`}
                  />
                ) : field.type === 'number' ? (
                  <input
                    type="number"
                    value={(nodeData[field.key] as number) ?? ''}
                    onChange={(e) => handleUpdate(field.key, e.target.value ? Number(e.target.value) : undefined)}
                    disabled={isDisabled}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                    placeholder={field.description || `输入 ${field.label}...`}
                  />
                ) : field.type === 'boolean' ? (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={(nodeData[field.key] as boolean) || false}
                      onChange={(e) => handleUpdate(field.key, e.target.checked)}
                      disabled={isDisabled}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-600">{field.description || field.label}</span>
                  </label>
                ) : (
                  // Fallback for array/object - use textarea with JSON
                  <textarea
                    value={typeof nodeData[field.key] === 'object'
                      ? JSON.stringify(nodeData[field.key], null, 2)
                      : (nodeData[field.key] as string) || ''}
                    onChange={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        handleUpdate(field.key, parsed);
                      } catch {
                        handleUpdate(field.key, e.target.value);
                      }
                    }}
                    rows={3}
                    disabled={isDisabled}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                    placeholder={field.description || `输入 ${field.label} (JSON 格式)...`}
                  />
                )}
                {field.description && field.type !== 'boolean' && (
                  <p className="text-xs text-gray-500 mt-1">{field.description}</p>
                )}
              </div>
            ))}
            <p className="text-xs text-gray-500">支持变量: {`{{变量名}}`} 引用输入字段</p>
          </div>
        ) : (
          // No schema: Default prompt textarea
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              图像描述 <span className="text-xs text-gray-400 font-normal">(prompt)</span>
              <span className="text-red-500 ml-0.5">*</span>
            </label>
            <textarea
              value={(nodeData.prompt as string) || ''}
              onChange={(e) => handleUpdate('prompt', e.target.value)}
              rows={4}
              disabled={isDisabled}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-purple-500 focus:border-purple-500 bg-white disabled:bg-gray-100"
              placeholder="描述你想要生成的图像...&#10;例如：一个穿着白裙子的女孩在樱花树下&#10;支持变量: {{变量名}}"
            />
            <p className="text-xs text-gray-500 mt-1">支持变量: {`{{变量名}}`} 引用输入字段</p>
          </div>
        )
      )}

      {/* Custom output fields without source - Manual mode only */}
      {/* These fields were added by user but have no value_source, so user needs to fill them */}
      {inputMode === 'manual' && customOutputFieldsWithoutSource.length > 0 && (
        <div className="space-y-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-sm font-medium text-green-700">
            <Plus className="w-4 h-4" />
            <span>自定义输出字段</span>
            <span className="text-xs text-green-500 font-normal">
              (无数据来源，需手动填写)
            </span>
          </div>
          {customOutputFieldsWithoutSource.map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span
                  className="inline-block w-2 h-2 rounded-full mr-1.5"
                  style={{ backgroundColor: getFieldTypeColor(field.type) }}
                />
                {field.label || field.key}
                {field.label && field.label !== field.key && (
                  <span className="text-xs text-gray-400 font-normal ml-1">({field.key})</span>
                )}
              </label>
              {field.type === 'string' || field.type === 'image_url' ? (
                <textarea
                  value={(nodeData[`__output_${field.key}`] as string) || ''}
                  onChange={(e) => handleUpdate(`__output_${field.key}`, e.target.value)}
                  rows={2}
                  disabled={isDisabled}
                  className="w-full px-3 py-2 border border-green-300 rounded-lg text-sm focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                  placeholder={`输入 ${field.label || field.key}...`}
                />
              ) : field.type === 'number' ? (
                <input
                  type="number"
                  value={(nodeData[`__output_${field.key}`] as number) ?? ''}
                  onChange={(e) => handleUpdate(`__output_${field.key}`, e.target.value ? Number(e.target.value) : undefined)}
                  disabled={isDisabled}
                  className="w-full px-3 py-2 border border-green-300 rounded-lg text-sm focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                  placeholder={`输入 ${field.label || field.key}...`}
                />
              ) : field.type === 'boolean' ? (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={(nodeData[`__output_${field.key}`] as boolean) || false}
                    onChange={(e) => handleUpdate(`__output_${field.key}`, e.target.checked)}
                    disabled={isDisabled}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-600">{field.label || field.key}</span>
                </label>
              ) : (
                // array/object - JSON input
                <textarea
                  value={typeof nodeData[`__output_${field.key}`] === 'object'
                    ? JSON.stringify(nodeData[`__output_${field.key}`], null, 2)
                    : (nodeData[`__output_${field.key}`] as string) || ''}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      handleUpdate(`__output_${field.key}`, parsed);
                    } catch {
                      handleUpdate(`__output_${field.key}`, e.target.value);
                    }
                  }}
                  rows={3}
                  disabled={isDisabled}
                  className="w-full px-3 py-2 border border-green-300 rounded-lg text-sm font-mono focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                  placeholder={`输入 ${field.label || field.key} (JSON 格式)...`}
                />
              )}
            </div>
          ))}
          <p className="text-xs text-green-600">
            提示: 在「输出字段」中为这些字段设置数据来源可跳过手动输入
          </p>
        </div>
      )}

      {/* Agent Mode */}
      {inputMode === 'agent' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              选择 Agent <span className="text-red-500">*</span>
              {agentsLoading && <span className="ml-2 text-gray-400 text-xs">加载中...</span>}
            </label>
            <select
              value={(nodeData.agent_type as string) || ''}
              onChange={(e) => handleUpdate('agent_type', e.target.value)}
              disabled={isDisabled || agentsLoading || agents.length === 0}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
            >
              <option value="">选择 Agent...</option>
              {agents.map((agent) => (
                <option key={agent.agent_type} value={agent.agent_type}>
                  {agent.name} ({agent.agent_type})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              用户意图 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={(nodeData.user_input as string) || ''}
              onChange={(e) => handleUpdate('user_input', e.target.value)}
              rows={3}
              disabled={isDisabled}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
              placeholder="告诉 Agent 你想要什么...&#10;支持变量: {{变量名}}"
            />
            <p className="text-xs text-gray-500 mt-1">支持变量: {`{{变量名}}`} 引用输入字段</p>
          </div>
        </div>
      )}

      {/* LLM Mode */}
      {inputMode === 'llm' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              选择 LLM 模型 <span className="text-red-500">*</span>
              {chatModelsLoading && <span className="ml-2 text-gray-400 text-xs">加载中...</span>}
            </label>
            <select
              value={(nodeData.llm_model as string) || ''}
              onChange={(e) => handleUpdate('llm_model', e.target.value)}
              disabled={isDisabled || chatModelsLoading || chatModels.length === 0}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
            >
              <option value="">选择模型...</option>
              {chatModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              温度 (Temperature): {((nodeData.llm_temperature as number) ?? 0.7).toFixed(1)}
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={(nodeData.llm_temperature as number) ?? 0.7}
              onChange={(e) => handleUpdate('llm_temperature', parseFloat(e.target.value))}
              disabled={isDisabled}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>精确 (0)</span>
              <span>平衡 (1)</span>
              <span>创意 (2)</span>
            </div>
          </div>

          {/* System Prompt Section - Collapsible */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowSystemPrompt(!showSystemPrompt)}
              className="w-full px-3 py-2 bg-gray-50 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-gray-500" />
                系统提示词
                {nodeData.system_prompt && (
                  <span className="text-xs text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">
                    已自定义
                  </span>
                )}
              </span>
              {showSystemPrompt ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>
            {showSystemPrompt && (
              <div className="p-3 space-y-3 bg-white">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">
                    选择模版加载或直接编辑
                  </label>
                  <PromptTemplateSelector
                    value={(nodeData.system_prompt as string) || ''}
                    onChange={(prompt) => handleUpdate('system_prompt', prompt)}
                    onReset={() => handleUpdate('system_prompt', undefined)}
                    language="zh"
                    placeholder="选择提示词模版..."
                    showReset={!!nodeData.system_prompt}
                  />
                </div>
                <textarea
                  value={
                    (nodeData.system_prompt as string) ||
                    promptTemplate?.system_prompts?.generate?.zh ||
                    ''
                  }
                  onChange={(e) => handleUpdate('system_prompt', e.target.value)}
                  rows={6}
                  disabled={isDisabled}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                  placeholder="系统提示词..."
                />
                <p className="text-xs text-gray-400">
                  提示: 选择模版后可继续编辑，或直接输入自定义提示词
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              用户消息 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={(nodeData.user_input as string) || ''}
              onChange={(e) => handleUpdate('user_input', e.target.value)}
              rows={3}
              disabled={isDisabled}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
              placeholder="输入你的请求...&#10;支持变量: {{变量名}}"
            />
            <p className="text-xs text-gray-500 mt-1">支持变量: {`{{变量名}}`} 引用输入字段</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InputModeRenderer;
